import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import LanguageSwitcher from './LanguageSwitcher'
import nocobaseService from '../services/nocobaseService'
import { useServices, useSubservices } from '../hooks/useAirtable'

// Group subservices under their main service (same logic as ServiceDetailPage)
function groupSubservicesByService(services, subservices) {
  if (!services?.length || !subservices?.length) return []
  return services.map(service => {
    const serviceId = service.id != null ? Number(service.id) : Number(service.Id)
    const related = subservices.filter(sub => {
      let subServiceId = null
      if (sub.Subservices !== undefined && sub.Subservices !== null) subServiceId = Number(sub.Subservices)
      else if (sub.parent_service_id != null) subServiceId = Number(sub.parent_service_id)
      else if (sub.main_service_id != null) subServiceId = Number(sub.main_service_id)
      else if (sub.service_id != null) subServiceId = Number(sub.service_id)
      else if (sub.parent_service != null) subServiceId = Number(sub.parent_service)
      if (subServiceId == null || isNaN(subServiceId)) return false
      return String(subServiceId) === String(serviceId) || Number(subServiceId) === Number(serviceId)
    })
    return { service, subservices: related }
  })
}

const Header = ({ onOpenChat }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false)
  const [dropdownClosing, setDropdownClosing] = useState(false)
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const [mobileServicesExpanded, setMobileServicesExpanded] = useState(false)
  const [hasInsights, setHasInsights] = useState(false)
  const [isCheckingInsights, setIsCheckingInsights] = useState(true)
  const { t } = useTranslation()
  const { isRTL: _isRTL, currentLanguage } = useLanguage()
  const { services } = useServices(true, currentLanguage)
  const { subservices } = useSubservices(null, true, currentLanguage)

  const servicesWithSubs = useMemo(
    () => groupSubservicesByService(services, subservices),
    [services, subservices]
  )

  const getServiceTitle = (s) => s?.title || s?.name || s?.title_en || s?.title_ar || ''
  const getSubserviceTitle = (s) => s?.title || s?.name || s?.title_en || s?.title_ar || ''

  // ظهور القائمة المنسدلة بسلاسة
  useEffect(() => {
    if (servicesDropdownOpen && !dropdownClosing) {
      const id = requestAnimationFrame(() => setDropdownVisible(true))
      return () => cancelAnimationFrame(id)
    }
    if (!servicesDropdownOpen) setDropdownVisible(false)
  }, [servicesDropdownOpen, dropdownClosing])

  const handleServicesMouseLeave = () => {
    if (!servicesDropdownOpen) return
    setServicesDropdownOpen(false)
    setDropdownClosing(true)
    setTimeout(() => setDropdownClosing(false), 220)
  }

  // Check if there are any published insights
  useEffect(() => {
    const checkInsights = async () => {
      try {
        setIsCheckingInsights(true)
        const allPosts = await nocobaseService.getPosts({ status: 'Published' })
        const hasPublishedPosts = allPosts && allPosts.length > 0
        setHasInsights(hasPublishedPosts)
        console.log('🔍 Header - Insights check:', { hasInsights: hasPublishedPosts, count: allPosts?.length || 0 })
      } catch (err) {
        // Silently handle network errors - don't show link if API fails
        if (err?.message?.includes('Failed to fetch') || err?.message?.includes('ERR_NETWORK')) {
          // Network error - silently fail
          setHasInsights(false)
        } else {
          // Other errors - log but don't show to user
          console.warn('⚠️ Header - Failed to check insights:', err?.message || err)
          setHasInsights(false)
        }
      } finally {
        setIsCheckingInsights(false)
      }
    }

    checkInsights()
  }, [])

  return (
    <header style={{
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 1000,
      width: 'calc(100% - 40px)',
      maxWidth: '1200px'
    }}>
      <div className="glass-container" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: '50px',
        padding: '12px 30px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease'
      }}>
        {/* Logo */}
        <Link to="/" style={{
          display: 'flex',
          alignItems: 'center',
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'opacity 0.3s'
        }}
        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
        onMouseLeave={(e) => e.target.style.opacity = '1'}>
          <img 
            src="/logo_wight.png" 
            alt="Almikial Law Company" 
            style={{
              height: 'clamp(30px, 4vw, 40px)',
              width: 'auto',
              objectFit: 'contain'
            }}
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
        </Link>

        {/* Desktop Navigation Menu */}
        <nav style={{
          display: 'flex',
          gap: '30px',
          alignItems: 'center'
        }}
        className="desktop-nav">
          <Link to="/" style={{
            color: 'rgba(255, 255, 255, 0.9)',
            textDecoration: 'none',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            fontWeight: '500',
            padding: '8px 16px',
            borderRadius: '20px',
            position: 'relative'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'white'
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'rgba(255, 255, 255, 0.9)'
            e.target.style.backgroundColor = 'transparent'
          }}>
            {t('header.nav.home')}
          </Link>
          <a href="#about" style={{
            color: 'rgba(255, 255, 255, 0.9)',
            textDecoration: 'none',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            fontWeight: '500',
            padding: '8px 16px',
            borderRadius: '20px'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'white'
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'rgba(255, 255, 255, 0.9)'
            e.target.style.backgroundColor = 'transparent'
          }}>
            {t('header.nav.about')}
          </a>
          {/* Services with dropdown: main services + subservices for quick navigation */}
          <div
            className="header-services-wrap"
            style={{ position: 'relative' }}
            onMouseEnter={() => { setServicesDropdownOpen(true); setDropdownClosing(false) }}
            onMouseLeave={handleServicesMouseLeave}
          >
            <span style={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '15px',
              fontWeight: '500',
              padding: '8px 16px',
              borderRadius: '20px',
              cursor: 'pointer',
              display: 'inline-block',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'white'
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
              e.currentTarget.style.backgroundColor = 'transparent'
            }}>
              {t('header.nav.services')} ▾
            </span>
            {/* جسر غير مرئي يملأ المسافة بين الهيدر والقائمة حتى لا يفقد الـ hover عند النزول */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                height: '20px'
              }}
            />
            {(servicesDropdownOpen || dropdownClosing) && servicesWithSubs.length > 0 && (
              <div className="header-services-dropdown" style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: dropdownClosing ? 'translateX(-50%) translateY(-6px)' : (dropdownVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-6px)'),
                marginTop: '20px',
                opacity: dropdownClosing ? 0 : (dropdownVisible ? 1 : 0),
                transition: 'opacity 0.22s ease, transform 0.22s ease',
                pointerEvents: dropdownClosing ? 'none' : 'auto',
                width: 'max-content',
                maxWidth: 'min(98vw, 1520px)',
                backgroundColor: 'rgba(8, 8, 8, 0.98)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
                padding: '24px 28px',
                zIndex: 1001,
                display: 'grid',
                gridTemplateColumns: servicesWithSubs.map(({ subservices: s }) => s.length > 5 ? '600px' : 'minmax(240px, max-content)').join(' '),
                gap: '8px 0',
                columnGap: '56px',
                alignItems: 'start'
              }}>
                {servicesWithSubs.map(({ service, subservices: subs }, idx) => (
                  <div
                    key={service.id || service.Id}
                    className="header-service-column"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      width: subs.length > 5 ? '600px' : undefined,
                      minWidth: 0,
                      maxWidth: subs.length > 5 ? '600px' : undefined,
                      overflow: 'hidden',
                      paddingInlineStart: idx > 0 ? '0' : 0,
                      paddingInlineEnd: 0,
                      borderInlineStart: idx > 0 ? '1px solid rgba(255, 255, 255, 0.15)' : 'none',
                      marginInlineStart: idx > 0 ? '28px' : 0
                    }}
                  >
                    <Link
                      to={`/${currentLanguage}/service/${service.id != null ? service.id : service.Id}`}
                      style={{
                        display: 'block',
                        color: 'rgba(255, 255, 255, 0.98)',
                        textDecoration: 'none',
                        fontSize: '15px',
                        fontWeight: '600',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)'
                        e.currentTarget.style.color = 'white'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.color = 'rgba(255, 255, 255, 0.98)'
                      }}
                    >
                      {getServiceTitle(service)}
                    </Link>
                    <div className="header-subservices-grid" style={{
                      display: 'grid',
                      gridTemplateColumns: subs.length > 5 ? '1fr 1fr' : '1fr',
                      gap: '6px 18px',
                      gridAutoRows: 'minmax(28px, auto)',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}>
                      {subs.map((sub) => (
                        <Link
                          key={sub.id || sub.Id}
                          to={`/${currentLanguage}/subservice/${sub.id != null ? sub.id : sub.Id}`}
                          style={{
                            display: 'block',
                            color: 'rgba(255, 255, 255, 0.82)',
                            textDecoration: 'none',
                            fontSize: '13px',
                            lineHeight: 1.35,
                            padding: '6px 10px',
                            paddingInlineStart: '14px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            boxSizing: 'border-box',
                            minWidth: 0
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'
                            e.currentTarget.style.color = 'white'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent'
                            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.82)'
                          }}
                        >
                          {getSubserviceTitle(sub)}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Only show Insights link if there are published posts */}
          {!isCheckingInsights && hasInsights && (
            <Link to={`/${currentLanguage}/insights`} style={{
              color: 'rgba(255, 255, 255, 0.9)',
              textDecoration: 'none',
              fontSize: '15px',
              transition: 'all 0.3s ease',
              fontWeight: '500',
              padding: '8px 16px',
              borderRadius: '20px'
            }}
            onMouseEnter={(e) => {
              e.target.style.color = 'white'
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.target.style.color = 'rgba(255, 255, 255, 0.9)'
              e.target.style.backgroundColor = 'transparent'
            }}>
              {currentLanguage === 'ar' ? 'مدونة التحديثات القانونية' : 'Insight'}
            </Link>
          )}
        </nav>

        {/* Desktop Right side - Location, Language Switcher and Get Consultation button */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '15px'
        }}
        className="desktop-icons">
          <a
            href="https://maps.app.goo.gl/w9noVjCEanJBkPTW6"
            target="_blank"
            rel="noopener noreferrer"
            title={t('common.viewOnMap')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.9)',
              padding: '8px',
              borderRadius: '50%',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'white'
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </a>
          <LanguageSwitcher />
          <button onClick={() => onOpenChat && onOpenChat()} style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '14px',
            transition: 'all 0.3s ease',
            fontWeight: '600',
            padding: '10px 24px',
            borderRadius: '25px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Inter, sans-serif',
            outline: 'none',
            minWidth: '160px',
            width: 'auto',
            maxWidth: '180px',
            height: '45px',
            minHeight: '45px',
            maxHeight: '45px',
            boxSizing: 'border-box',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            lineHeight: '1'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)'
            e.target.style.transform = 'translateY(-1px)'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)'
            e.target.style.transform = 'translateY(0)'
          }}>
            {t('common.contactUs')}
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '8px',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent'
          }}>
          ☰
        </button>
      </div>

      {/* Mobile Navigation Menu */}
      <div 
        className="mobile-nav"
        style={{
          display: isMobileMenuOpen ? 'block' : 'none',
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 40px)',
          maxWidth: '400px',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(20px)',
          borderRadius: '25px',
          padding: '25px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          zIndex: 999
        }}>
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <a href="#home" style={{
            color: 'rgba(255, 255, 255, 0.9)',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'all 0.3s ease',
            padding: '12px 20px',
            fontWeight: '500',
            borderRadius: '15px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'white'
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'rgba(255, 255, 255, 0.9)'
            e.target.style.backgroundColor = 'transparent'
          }}
          onClick={() => setIsMobileMenuOpen(false)}>
            {t('header.nav.home')}
          </a>
          <a href="#about" style={{
            color: 'rgba(255, 255, 255, 0.9)',
            textDecoration: 'none',
            fontSize: '16px',
            transition: 'all 0.3s ease',
            padding: '12px 20px',
            fontWeight: '500',
            borderRadius: '15px',
            textAlign: 'center'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'white'
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'rgba(255, 255, 255, 0.9)'
            e.target.style.backgroundColor = 'transparent'
          }}
          onClick={() => setIsMobileMenuOpen(false)}>
            {t('header.nav.about')}
          </a>
          {/* Mobile: Services expandable - main + sub for quick navigation */}
          <div style={{ width: '100%' }}>
            <button
              type="button"
              onClick={() => setMobileServicesExpanded(!mobileServicesExpanded)}
              style={{
                width: '100%',
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                padding: '12px 20px',
                fontWeight: '500',
                borderRadius: '15px',
                textAlign: 'center',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {t('header.nav.services')} {mobileServicesExpanded ? '▴' : '▾'}
            </button>
            {mobileServicesExpanded && servicesWithSubs.length > 0 && (
              <div style={{
                paddingInlineStart: '16px',
                paddingInlineEnd: '8px',
                paddingBottom: '8px'
              }}>
                {servicesWithSubs.map(({ service, subservices: subs }) => (
                  <div key={service.id || service.Id}>
                    <Link
                      to={`/${currentLanguage}/service/${service.id != null ? service.id : service.Id}`}
                      onClick={() => { setIsMobileMenuOpen(false); setMobileServicesExpanded(false) }}
                      style={{
                        display: 'block',
                        color: 'rgba(255, 255, 255, 0.95)',
                        textDecoration: 'none',
                        fontSize: '15px',
                        fontWeight: '600',
                        padding: '8px 12px',
                        borderRadius: '10px'
                      }}
                    >
                      {getServiceTitle(service)}
                    </Link>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: subs.length > 5 ? '1fr 1fr' : '1fr',
                      gap: '4px 12px'
                    }}>
                      {subs.map((sub) => (
                        <Link
                          key={sub.id || sub.Id}
                          to={`/${currentLanguage}/subservice/${sub.id != null ? sub.id : sub.Id}`}
                          onClick={() => { setIsMobileMenuOpen(false); setMobileServicesExpanded(false) }}
                          style={{
                            display: 'block',
                            color: 'rgba(255, 255, 255, 0.8)',
                            textDecoration: 'none',
                            fontSize: '14px',
                            padding: '6px 12px',
                            paddingInlineStart: '20px',
                            borderRadius: '8px'
                          }}
                        >
                          {getSubserviceTitle(sub)}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Only show Insights link if there are published posts */}
          {!isCheckingInsights && hasInsights && (
            <Link 
              to={`/${currentLanguage}/insights`}
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                textDecoration: 'none',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                padding: '12px 20px',
                fontWeight: '500',
                borderRadius: '15px',
                textAlign: 'center'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'white'
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'rgba(255, 255, 255, 0.9)'
                e.target.style.backgroundColor = 'transparent'
              }}
              onClick={() => setIsMobileMenuOpen(false)}>
              {currentLanguage === 'ar' ? 'مدونة التحديثات القانونية' : 'Insight'}
            </Link>
          )}
        </nav>

        {/* Location link in Mobile Menu */}
        <a
          href="https://maps.app.goo.gl/w9noVjCEanJBkPTW6"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '16px',
            color: 'rgba(255, 255, 255, 0.9)',
            textDecoration: 'none',
            fontSize: '15px',
            padding: '12px 20px',
            borderRadius: '15px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'white'
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {t('common.viewOnMap')}
        </a>

        {/* Language Switcher in Mobile Menu - so Arabic is available on mobile */}
        <div style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <LanguageSwitcher />
        </div>
        
        {/* Get Consultation Button in Mobile Menu */}
        <div style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <button onClick={() => {
            onOpenChat && onOpenChat()
            setIsMobileMenuOpen(false)
          }} style={{
            color: 'white',
            textDecoration: 'none',
            fontSize: '14px',
            transition: 'all 0.3s ease',
            fontWeight: '600',
            padding: '10px 24px',
            borderRadius: '25px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
            outline: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent',
            minWidth: '160px',
            width: 'auto',
            maxWidth: '180px'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)'
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)'
          }}>
            {t('common.contactUs')}
          </button>
        </div>
      </div>

      <style>{`
        /* خدمات: قائمة عريضة أفقية (ميجا منيو) - أعمدة بدل قائمة طويلة */
        .header-services-dropdown {
          min-height: 0;
        }
        .header-service-column {
          isolation: isolate;
        }
        .header-service-column * {
          max-width: 100%;
        }
        .header-service-column {
          box-sizing: border-box;
        }
        .header-subservices-grid {
          contain: layout;
          width: 100%;
        }
        .header-subservices-grid > * {
          min-width: 0;
          max-width: 100%;
        }
        @media (max-width: 900px) {
          .header-services-dropdown {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .desktop-nav,
          .desktop-icons {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .glass-container {
            padding: 10px 20px !important;
            border-radius: 25px !important;
          }
          header {
            top: 10px !important;
            width: calc(100% - 20px) !important;
          }
        }
        @media (min-width: 769px) {
          .mobile-nav {
            display: none !important;
          }
        }
      `}</style>
    </header>
  )
}

export default Header
